import { DateTime } from "luxon";

import { SHIFT_ORDINAL, ShiftLetter } from "../../types/Shift";

export interface ShiftLockState {
  currentShift: {
    date: DateTime;
    dayOrNight: ShiftLetter;
  };
  lockAfterDays: number;
  manuallyUnlockedShifts: {
    date: DateTime;
    dayOrNight: ShiftLetter;
  }[];
}

function dayNumber(date: DateTime): number {
  return Math.floor(date.startOf("day").toMillis() / 86400000);
}

/**
 * Ranks a shift in time. Previously `dayIndex * 2 + (shift === "N" ? 1 : 0)`, which had no room for the
 * afternoon shift - an "A" fell into the day shift's slot, so the two ranked as the same moment.
 */
function shiftIndex(date: DateTime | null, shift: ShiftLetter): number {
  if (!date) return 0;
  return dayNumber(date) * 3 + SHIFT_ORDINAL[shift];
}

/**
 * Mirrors the server's rule, which is whole days and does not consider the shift at all:
 * `shift.Date <= latestShift.Date.AddDays(-LockShiftsAfterDaysFromLatest)`
 * (ShiftRepository.IsShiftLockedAsync).
 *
 * Note: this used to compare shift indices against `lockAfterDays * 2`, which disagreed with the server
 * whenever the two shifts' ordinals differed - a night shift exactly lockAfterDays back read as editable
 * here while the server locked it, so the edit was offered and then rejected.
 */
function isAutoLocked(
  targetDate: DateTime,
  current: { date: DateTime },
  lockAfterDays: number
): boolean {
  return dayNumber(current.date) - dayNumber(targetDate) >= lockAfterDays;
}

function isFutureShift(
  targetDate: DateTime,
  targetShift: ShiftLetter,
  current: { date: DateTime; shift: ShiftLetter }
): boolean {
  return (
    shiftIndex(targetDate, targetShift) >
    shiftIndex(current.date, current.shift)
  );
}

function isManuallyUnlocked(
  date: DateTime,
  shift: ShiftLetter,
  unlocked: { date: DateTime; dayOrNight: ShiftLetter }[]
): boolean {
  return unlocked.some(
    s =>
      s.dayOrNight === shift &&
      s.date.hasSame(date, "day")
  );
}

export function isShiftEditable(
  date: DateTime | null,
  shift: ShiftLetter,
  state: ShiftLockState | null
): boolean {
  if (!state || !date) return true;

  const current = {
    date: state.currentShift.date,
    shift: state.currentShift.dayOrNight
  };

  if (isFutureShift(date, shift, current)) {
    return false;
  }

  if (isManuallyUnlocked(date, shift, state.manuallyUnlockedShifts)) {
    return true;
  }

  return !isAutoLocked(date, current, state.lockAfterDays);
}

export function isDayEditable(
  date: DateTime | null,
  state: ShiftLockState | null
): boolean {
  if (!state || !date) return true;

  // Note: the afternoon shift counts. A day whose only unlocked shift was the afternoon one read as
  // read-only, matching IsDayWritableAsync on the server, which has the same gap.
  return (
    isShiftEditable(date, "D", state) ||
    isShiftEditable(date, "A", state) ||
    isShiftEditable(date, "N", state)
  );
}

export function isCurrentShift(date: DateTime | null, shift: ShiftLetter, state: ShiftLockState | null): boolean {
  if (!state || !date) return false;

  return (
    state.currentShift.date.hasSame(date, "day") &&
    state.currentShift.dayOrNight === shift
  );
}

/**
 * True when the given shift is later than the system's current ("latest") shift.
 * Used to prevent selecting/editing a shift that has not started yet.
 * Defaults to false when the lock state or date is unknown, so the shift stays selectable.
 */
export function isShiftInFuture(
  date: DateTime | null,
  shift: ShiftLetter,
  state: ShiftLockState | null
): boolean {
  if (!state || !date) return false;

  return isFutureShift(date, shift, {
    date: state.currentShift.date,
    shift: state.currentShift.dayOrNight,
  });
}
